/**
 * SystemInfoView -- Estado del sistema: versiones API y Panel, entorno, conexión.
 * @module presentation/views/SystemInfoView
 */

import { AppConfig } from '../../config.js';
import { confirmar } from '../shared/nxDialogo.js';
import { ApiHealthClient } from '../../infrastructure/api/ApiHealthClient.js';
import { regresoHtml } from '../shared/regreso.js';

/* global __APP_VERSION__ */
const PANEL_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '2.2.0';

export async function renderSystemInfo(container) {
  container.innerHTML = `
    <section class="system-info">
      ${regresoHtml()}
      <h1>Estado del sistema</h1>
      <p class="subtitle">Versiones, conexión y entorno activo</p>
      <div class="sys-grid">
        <div class="sys-card">
          <div class="sys-card-title">Panel</div>
          <div class="sys-card-value" id="si-panel-ver">v${PANEL_VERSION}</div>
          <div class="sys-card-sub">Nexo Panel</div>
        </div>
        <div class="sys-card" id="si-api-card">
          <div class="sys-card-title">API</div>
          <div class="sys-card-value" id="si-api-ver"><span class="sys-loading">...</span></div>
          <div class="sys-card-sub" id="si-api-status">Verificando...</div>
        </div>
        <div class="sys-card">
          <div class="sys-card-title">Entorno</div>
          <div class="sys-card-value" id="si-env">${AppConfig.env || 'production'}</div>
          <div class="sys-card-sub" id="si-api-url">${AppConfig.apiBase || 'proxy'}</div>
        </div>
        <div class="sys-card" id="si-time-card">
          <div class="sys-card-title">Hora servidor</div>
          <div class="sys-card-value" id="si-time"><span class="sys-loading">...</span></div>
          <div class="sys-card-sub">UTC</div>
        </div>
      </div>

      <div class="sys-section">
        <h2>Servicios</h2>
        <div class="sys-services" id="si-services">
          <div class="sys-service-row"><span class="sys-service-name">API</span><span class="sys-badge" id="svc-api">...</span></div>
          <div class="sys-service-row"><span class="sys-service-name">PostgreSQL</span><span class="sys-badge" id="svc-db">...</span></div>
          <div class="sys-service-row"><span class="sys-service-name">Firebase</span><span class="sys-badge" id="svc-firebase">...</span></div>
        </div>
      </div>

      <div class="sys-section">
        <h2>Accesos directos</h2>
        <div class="sys-links">
          <a href="https://easypanel.nexodigital360.com" target="_blank" rel="noopener" class="sys-link">EasyPanel (VPS)</a>
          <a href="https://n8n.nexodigital360.com" target="_blank" rel="noopener" class="sys-link">N8N</a>
          <a href="https://api.nexodigital360.com/swagger" target="_blank" rel="noopener" class="sys-link">API Docs (Swagger)</a>
          <a href="https://api.nexodigital360.com/api/health" target="_blank" rel="noopener" class="sys-link">Health Endpoint</a>
        </div>
      </div>

      <div class="sys-actions" style="display:flex;gap:8px">
        <button class="btn-ghost" id="btn-si-refresh" type="button">Actualizar datos</button>
        <button class="btn-ghost" id="btn-si-hard-refresh" type="button" style="color:var(--accent-red)">Forzar recarga panel</button>
      </div>
    </section>`;

  await loadSystemInfo();

  document.getElementById('btn-si-refresh')?.addEventListener('click', loadSystemInfo);
  document.getElementById('btn-si-hard-refresh')?.addEventListener('click', async () => {
    if (await confirmar({ titulo: 'Recargar el panel desde cero', texto: 'Se limpia la cache y se vuelve a cargar todo.', aceptar: 'Recargar' })) {
      window.location.reload(true);
    }
  });
}

async function loadSystemInfo() {
  try {
    const res = await ApiHealthClient.leer();
    if (res.ok) {
      const data = await res.json();
      const ver = data.version?.split('+')[0] ?? '?';
      const uptime = data.uptime != null ? `${Math.floor(data.uptime / 60)}m uptime` : '';

      document.getElementById('si-api-ver').textContent = `v${ver}`;
      document.getElementById('si-api-status').textContent = `${data.status} ${uptime}`;
      document.getElementById('si-api-card').classList.toggle('sys-card--ok', data.status === 'healthy');

      document.getElementById('svc-api').textContent = data.status === 'healthy' ? 'OK' : 'ERROR';
      document.getElementById('svc-api').className = `sys-badge sys-badge--${data.status === 'healthy' ? 'ok' : 'err'}`;

      if (data.services) {
        const db = data.services?.database ?? data.services?.postgres;
        const fb = data.services?.firebase;
        setBadge('svc-db', db);
        setBadge('svc-firebase', fb);
      }

      if (data.timestamp) {
        const t = new Date(data.timestamp);
        document.getElementById('si-time').textContent = t.toISOString().replace('T', ' ').substring(0, 19);
      }
    } else {
      setApiError();
    }
  } catch {
    setApiError();
  }
}

function setApiError() {
  document.getElementById('si-api-ver').textContent = 'Error';
  document.getElementById('si-api-status').textContent = 'Sin conexión';
  document.getElementById('si-api-card').classList.add('sys-card--err');
  document.getElementById('svc-api').textContent = 'ERROR';
  document.getElementById('svc-api').className = 'sys-badge sys-badge--err';
}

function setBadge(id, value) {
  const el = document.getElementById(id);
  if (!el) return;
  if (value == null) { el.textContent = '?'; el.className = 'sys-badge'; return; }
  const ok = value === true || value === 'ok' || value === 'healthy';
  el.textContent = ok ? 'OK' : 'Error';
  el.className = `sys-badge sys-badge--${ok ? 'ok' : 'err'}`;
}
